import { Decimal } from 'decimal.js';

import { roiGross, roundAndRebalance } from '../lib/calc';
import { tapeOf, type Tape } from '../lib/feedContext';
import type { Meta } from './meta';
import { reviewCard, type ReviewCard } from './review';
import type { FeedRow } from './rows';
import type { ShowUnit } from './show';

export { tapeOf, type Tape };

const THIN = '\u2009';
const SHIFT = 4 * 60 * 60 * 1000;

export type ReportMark = 'open' | 'hit' | 'miss';
export type GainCur = '' | 'rub' | 'usd';
export type ReportPeriod = 'day' | 'week' | 'all';

export interface ReportLeg {
  book: string;
  code: string;
  side: string;
  odds: string;
  stake: string;
  link?: string;
}

export interface ReportRow {
  id: string;
  forkId: string;
  match: string;
  sport: string;
  market: string;
  roi: string;
  gain: string;
  gainAmount: string;
  gainCur: GainCur;
  legs: ReportLeg[];
  mark: ReportMark;
  at: string;
  markedAt: string | null;
  note: string;
  links: string[];
  origin: 'feed' | 'hand';
  playedOn: string;
  tape: Tape;
  shared?: boolean;
  /** Учебная строка. В плюс, процент и круги не входит. */
  sample?: boolean;
}

export function isSample(row: Pick<ReportRow, 'id' | 'forkId'> & { sample?: boolean }): boolean {
  return row.sample === true || row.id.startsWith('ex-') || row.forkId.startsWith('ex-');
}

/** Один и тот же пример у каждого, чтобы пустой журнал было на что посмотреть. */
export function exampleRow(now: number): ReportRow {
  return {
    id: 'ex-spirit-furia-0001',
    forkId: 'ex-spirit-furia-0001',
    match: 'Spirit — FURIA',
    sport: 'киберспорт',
    market: '',
    roi: '5.00',
    gain: '',
    gainAmount: '1000.00',
    gainCur: 'rub',
    legs: [
      { book: 'Pinnacle', code: 'pinnacle', side: 'Spirit', odds: '2.100', stake: '10 000' },
      { book: 'Polymarket', code: 'polymarket', side: 'FURIA', odds: '2.100', stake: '10 000' },
    ],
    mark: 'hit',
    at: new Date(now).toISOString(),
    markedAt: null,
    note: '',
    links: [],
    origin: 'hand',
    playedOn: dayKey(now),
    tape: 'hand',
    sample: true,
  };
}

export const TAPE_LABEL: Record<Exclude<Tape, ''>, string> = {
  app: 'своя',
  tests: '2.0',
  feed3: '3.0',
  cupis: 'CUPIS',
  ru: 'русские',
  transfers: 'переливы',
  predict: 'предикт',
  hand: 'руками',
};

export function tapeLabel(tape: string): string {
  if (tape && tape in TAPE_LABEL) return TAPE_LABEL[tape as Exclude<Tape, ''>];
  return '';
}

export interface ReportBody extends ReviewCard {
  gainAmount: string;
  gainCur: GainCur;
  tape: Tape;
}

export interface Plus {
  cur: 'rub' | 'usd';
  amount: string;
}

export interface Tally {
  taken: number;
  open: number;
  hit: number;
  miss: number;
  rate: string | null;
  avgRoi: string | null;
  plus: Plus[];
  streak: number;
  books: string[];
}

const IDS = 'forq.report.ids';

export function reportBody(
  row: FeedRow,
  meta: Meta,
  fx: number,
  unit: ShowUnit,
  pathname = typeof location === 'undefined' ? '' : location.pathname,
): ReportBody | null {
  const card = reviewCard(row, meta, fx, unit);
  if (card === null) return null;
  const tape = tapeOf(pathname);
  const profit = row.row.profit;
  if (typeof profit !== 'number' || !Number.isFinite(profit) || profit < 0) {
    return { ...card, gainAmount: '', gainCur: '', tape };
  }
  const base = new Decimal(profit);
  if (unit === 'usd' && fx > 0) {
    return {
      ...card,
      gainAmount: base.div(fx).toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toFixed(2),
      gainCur: 'usd',
      tape,
    };
  }
  return {
    ...card,
    gainAmount: base.toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toFixed(2),
    gainCur: 'rub',
    tape,
  };
}

export function dayKey(at: string | number, shift = SHIFT): string {
  const ms = typeof at === 'number' ? at : Date.parse(at);
  const d = new Date(ms + shift);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function rowDay(row: Pick<ReportRow, 'at' | 'playedOn'>): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(row.playedOn || '')) return row.playedOn;
  return dayKey(row.at);
}

function rowInstant(row: Pick<ReportRow, 'at' | 'playedOn'>): number {
  if (/^\d{4}-\d{2}-\d{2}$/.test(row.playedOn || '')) {
    const [year, month, day] = row.playedOn.split('-').map(Number);
    return Date.UTC(year ?? 1970, (month ?? 1) - 1, day) - SHIFT;
  }
  return Date.parse(row.at);
}

export function inPeriod(row: ReportRow, period: ReportPeriod, now: number): boolean {
  if (period === 'all') return true;
  const at = rowInstant(row);
  if (!Number.isFinite(at)) return false;
  if (period === 'day') return rowDay(row) === dayKey(now);
  return at >= now - 7 * 24 * 60 * 60 * 1000;
}

function rateOf(hit: number, miss: number): string | null {
  const settled = hit + miss;
  if (settled === 0) return null;
  const pct = new Decimal(hit).div(settled).times(100);
  const shown = pct.toDecimalPlaces(1, Decimal.ROUND_HALF_EVEN).toFixed(1);
  return shown.endsWith('.0') ? shown.slice(0, -2) : shown;
}

function numberOf(raw: string): Decimal | null {
  if (!raw.trim()) return null;
  try {
    const value = new Decimal(raw);
    return value.isFinite() ? value : null;
  } catch {
    return null;
  }
}

function avgOf(rows: ReportRow[]): string | null {
  const values = rows.flatMap((row) => {
    const value = numberOf(row.roi);
    return value === null ? [] : [value];
  });
  if (values.length === 0) return null;
  const sum = values.reduce((acc, value) => acc.plus(value), new Decimal(0));
  return sum.div(values.length).toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toFixed(2);
}

function plusOf(rows: ReportRow[]): Plus[] {
  const bags = new Map<'rub' | 'usd', Decimal>();
  for (const row of rows) {
    if (row.mark === 'open' || (row.gainCur !== 'rub' && row.gainCur !== 'usd')) continue;
    const amount = numberOf(row.gainAmount);
    if (amount === null) continue;
    const prev = bags.get(row.gainCur) ?? new Decimal(0);
    bags.set(row.gainCur, prev.plus(amount));
  }
  return [...bags.entries()].map(([cur, amount]) => ({
    cur,
    amount: amount.toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toFixed(2),
  }));
}

function streakOf(rows: ReportRow[]): number {
  const marked = rows
    .filter((row) => row.mark !== 'open')
    .sort((a, b) => Date.parse(b.markedAt || b.at) - Date.parse(a.markedAt || a.at));
  let streak = 0;
  for (const row of marked) {
    if (row.mark !== 'hit') break;
    streak += 1;
  }
  return streak;
}

function booksOf(rows: ReportRow[]): string[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const seen = new Set<string>();
    for (const leg of row.legs) {
      const name = leg.book.trim();
      if (!name || seen.has(name)) continue;
      seen.add(name);
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 3)
    .map(([name]) => name);
}

export function tally(rows: ReportRow[], period: ReportPeriod, now: number): Tally {
  const picked = rows.filter((row) => inPeriod(row, period, now));
  const hit = picked.filter((row) => row.mark === 'hit').length;
  const miss = picked.filter((row) => row.mark === 'miss').length;
  return {
    taken: picked.length,
    open: picked.filter((row) => row.mark === 'open').length,
    hit,
    miss,
    rate: rateOf(hit, miss),
    avgRoi: avgOf(picked),
    plus: plusOf(picked),
    streak: streakOf(picked),
    books: booksOf(picked),
  };
}

/** Сохранённая из ленты или без сумм/кэфов — сверить с тем, что приняла касса. */
export function needsCheck(row: Pick<ReportRow, 'mark' | 'origin' | 'legs' | 'id' | 'forkId'> & { sample?: boolean }): boolean {
  if (isSample(row) || row.mark !== 'open') return false;
  if (row.origin === 'feed') return true;
  return row.legs.some((leg) => !leg.stake.trim() || !leg.odds.trim());
}

/** Яркость знака в журнале. 8% и выше — полная, тоньше — тише. Это краска, не деньги. */
export function fatHeat(roi: string): number {
  let value: Decimal;
  try {
    value = new Decimal(roi);
  } catch {
    return 0;
  }
  if (!value.isFinite() || value.lte(0)) return 0;
  return Decimal.min(value.div(8), 1).toDecimalPlaces(3, Decimal.ROUND_HALF_EVEN).toNumber();
}

export function moneyLine(amount: string, cur: 'rub' | 'usd'): string {
  let value: Decimal;
  try {
    value = new Decimal(amount);
  } catch {
    return '—';
  }
  if (!value.isFinite()) return '—';
  const sign = value.isNegative() ? '−' : '+';
  const abs = value.abs();
  if (cur === 'rub' && abs.toDecimalPlaces(2).mod(1).isZero()) {
    const body = abs.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, THIN);
    return `${sign}${body} ₽`;
  }
  const [whole, frac] = abs.toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toFixed(2).split('.');
  const body = (whole ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, THIN);
  const unit = cur === 'rub' ? '₽' : '$';
  return `${sign}${body}.${frac ?? '00'} ${unit}`;
}

export function remembered(forkId: string): boolean {
  try {
    const raw = JSON.parse(localStorage.getItem(IDS) || '[]') as unknown;
    return Array.isArray(raw) && raw.includes(forkId);
  } catch {
    return false;
  }
}

export function remember(forkId: string): void {
  try {
    const raw = JSON.parse(localStorage.getItem(IDS) || '[]') as unknown;
    const ids = Array.isArray(raw) ? raw.filter((item) => typeof item === 'string') : [];
    localStorage.setItem(IDS, JSON.stringify([forkId, ...ids.filter((id) => id !== forkId)].slice(0, 400)));
  } catch {
    /* отчёт на сервере всё равно есть */
  }
}

export function forget(forkId: string): void {
  try {
    const raw = JSON.parse(localStorage.getItem(IDS) || '[]') as unknown;
    const ids = Array.isArray(raw) ? raw.filter((item) => item !== forkId) : [];
    localStorage.setItem(IDS, JSON.stringify(ids));
  } catch {
    /* карточка просто снова предложит «в отчёт» */
  }
}

export interface Slice {
  name: string;
  taken: number;
  hit: number;
  miss: number;
  rate: string | null;
  plus: Plus[];
}

function sliceOf(rows: ReportRow[], nameOf: (row: ReportRow) => string[]): Slice[] {
  const bags = new Map<string, ReportRow[]>();
  for (const row of rows) {
    const names = new Set(nameOf(row).map((name) => name.trim()).filter(Boolean));
    for (const name of names) {
      const list = bags.get(name) ?? [];
      list.push(row);
      bags.set(name, list);
    }
  }
  return [...bags.entries()]
    .map(([name, group]) => {
      const hit = group.filter((row) => row.mark === 'hit').length;
      const miss = group.filter((row) => row.mark === 'miss').length;
      return { name, taken: group.length, hit, miss, rate: rateOf(hit, miss), plus: plusOf(group) };
    })
    .sort((a, b) => b.taken - a.taken || a.name.localeCompare(b.name));
}

export function bySport(rows: ReportRow[]): Slice[] {
  return sliceOf(rows, (row) => [row.sport || 'без спорта']);
}

export function byTape(rows: ReportRow[]): Slice[] {
  return sliceOf(rows, (row) => [tapeLabel(row.tape) || 'без ленты']);
}

export function byBook(rows: ReportRow[]): Slice[] {
  return sliceOf(rows, (row) => row.legs.map((leg) => leg.book));
}

export function monthSpan(now: number, which: 'this' | 'prev'): { from: string; to: string } {
  const today = dayKey(now);
  const [year, month] = today.split('-').map(Number);
  const y = year ?? 1970;
  const m = month ?? 1;
  if (which === 'this') return { from: `${today.slice(0, 8)}01`, to: today };
  const prevMonth = m === 1 ? 12 : m - 1;
  const prevYear = m === 1 ? y - 1 : y;
  const last = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return { from: `${prevYear}-${pad(prevMonth)}-01`, to: `${prevYear}-${pad(prevMonth)}-${pad(last)}` };
}

export function inSpan(row: Pick<ReportRow, 'at' | 'playedOn'>, from: string, to: string): boolean {
  const day = rowDay(row);
  return day >= from && day <= to;
}

const DAYPARTS = [
  { name: 'ночь', from: 0, to: 6 },
  { name: 'утро', from: 6, to: 12 },
  { name: 'день', from: 12, to: 18 },
  { name: 'вечер', from: 18, to: 24 },
] as const;

export function clockHour(row: Pick<ReportRow, 'at'>): number {
  const ms = Date.parse(row.at);
  if (!Number.isFinite(ms)) return 0;
  return new Date(ms + SHIFT).getUTCHours();
}

/** Четыре части суток по часу записи, UTC+4. Плюс — только у зашедших. */
export function byClock(rows: ReportRow[]): Slice[] {
  return DAYPARTS.map((part) => {
    const group = rows.filter((row) => {
      const hour = clockHour(row);
      return hour >= part.from && hour < part.to;
    });
    const hit = group.filter((row) => row.mark === 'hit').length;
    const miss = group.filter((row) => row.mark === 'miss').length;
    return { name: part.name, taken: group.length, hit, miss, rate: rateOf(hit, miss), plus: plusOf(group) };
  });
}

export function matchBoard(rows: ReportRow[]): { better: Slice[]; worse: Slice[] } {
  const ranked = sliceOf(rows, (row) => [row.match])
    .filter((item) => item.hit + item.miss > 0)
    .sort((a, b) => {
      const left = a.rate === null ? -1 : Number(a.rate);
      const right = b.rate === null ? -1 : Number(b.rate);
      return right - left || b.taken - a.taken || a.name.localeCompare(b.name);
    });
  const take = Math.min(5, Math.max(1, Math.ceil(ranked.length / 2)));
  const better = ranked.slice(0, ranked.length === 0 ? 0 : take);
  const names = new Set(better.map((item) => item.name));
  const worse = [...ranked].reverse().filter((item) => !names.has(item.name)).slice(0, 5);
  return { better, worse };
}

export interface DayBar {
  key: string;
  label: string;
  taken: number;
  hit: number;
}

export function inMonth(row: Pick<ReportRow, 'at' | 'playedOn'>, now: number): boolean {
  return rowDay(row).slice(0, 7) === dayKey(now).slice(0, 7);
}

function parseMoney(raw: string): Decimal | null {
  const text = raw.replace(/\s/g, '').replace(',', '.').replace(/[₽$]/g, '');
  if (!text) return null;
  try {
    const value = new Decimal(text);
    if (!value.isFinite() || value.lt(0)) return null;
    return value;
  } catch {
    return null;
  }
}

function parseOdds(raw: string): Decimal | null {
  const value = parseMoney(raw);
  if (value === null || value.lte(1)) return null;
  return value.toDecimalPlaces(3, Decimal.ROUND_DOWN);
}

function qty(value: Decimal): string {
  return value.toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toFixed(2);
}

export interface ForkQuote {
  roiPct: string;
  amount: string | null;
  fork: boolean;
}

/** Профит вилки из двух кэфов и двух сумм. Худшая выплата минус поставленное. */
export function forkQuote(odds: [string, string], stakes: [string, string]): ForkQuote | null {
  const left = parseOdds(odds[0]);
  const right = parseOdds(odds[1]);
  if (left === null || right === null) return null;
  const stakeLeft = parseMoney(stakes[0]);
  const stakeRight = parseMoney(stakes[1]);
  if (stakeLeft !== null && stakeRight !== null && stakeLeft.plus(stakeRight).gt(0)) {
    const payLeft = stakeLeft.times(left);
    const payRight = stakeRight.times(right);
    const total = stakeLeft.plus(stakeRight);
    const worst = payLeft.lt(payRight) ? payLeft : payRight;
    const profit = worst.minus(total);
    return { roiPct: qty(profit.div(total).times(100)), amount: qty(profit), fork: profit.gt(0) };
  }
  const gross = roiGross([left, right]).times(100);
  return { roiPct: qty(gross), amount: null, fork: gross.gt(0) };
}

function stakeText(value: Decimal): string {
  if (value.toDecimalPlaces(2).mod(1).isZero()) return value.toFixed(0);
  return qty(value);
}

/** Раскладка банка по двум кэфам. Суммы можно потом поправить руками. */
export function splitStakes(bank: string, odds: [string, string], step: string): [string, string] | null {
  const money = parseMoney(bank);
  const left = parseOdds(odds[0]);
  const right = parseOdds(odds[1]);
  if (money === null || left === null || right === null || money.lte(0)) return null;
  const plan = roundAndRebalance(money, [left, right], step);
  return [stakeText(plan.stakes[0] ?? new Decimal(0)), stakeText(plan.stakes[1] ?? new Decimal(0))];
}

export interface CurvePoint {
  day: string;
  amount: string;
  cum: string;
}

/** Накопленный плюс зашедших вилок с 1-го числа до сегодня, UTC+4. */
export function monthCurve(rows: ReportRow[], now: number, cur: 'rub' | 'usd'): CurvePoint[] {
  const month = dayKey(now).slice(0, 7);
  const today = Number(dayKey(now).slice(8));
  const bags = new Map<number, Decimal>();
  for (const row of rows) {
    if (row.mark === 'open' || row.gainCur !== cur) continue;
    const amount = numberOf(row.gainAmount);
    if (amount === null) continue;
    const key = rowDay(row);
    if (!key.startsWith(`${month}-`)) continue;
    const day = Number(key.slice(8));
    bags.set(day, (bags.get(day) ?? new Decimal(0)).plus(amount));
  }
  const out: CurvePoint[] = [];
  let cum = new Decimal(0);
  for (let day = 1; day <= today; day += 1) {
    const amount = bags.get(day) ?? new Decimal(0);
    cum = cum.plus(amount);
    out.push({ day: String(day), amount: qty(amount), cum: qty(cum) });
  }
  return out;
}

function nextDay(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + 1));
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** Накопленный итог по дням выбранного отрезка. Подпись дня — `DD.MM`. */
export function rangeCurve(rows: ReportRow[], from: string, to: string, cur: 'rub' | 'usd'): CurvePoint[] {
  const bags = new Map<string, Decimal>();
  for (const row of rows) {
    if (row.mark === 'open' || row.gainCur !== cur) continue;
    const amount = numberOf(row.gainAmount);
    if (amount === null) continue;
    const key = rowDay(row);
    if (key < from || key > to) continue;
    bags.set(key, (bags.get(key) ?? new Decimal(0)).plus(amount));
  }
  const out: CurvePoint[] = [];
  let cum = new Decimal(0);
  let cursor = from;
  let guard = 0;
  while (cursor <= to && guard < 62) {
    const amount = bags.get(cursor) ?? new Decimal(0);
    cum = cum.plus(amount);
    const [, month, day] = cursor.split('-');
    out.push({ day: `${day}.${month}`, amount: qty(amount), cum: qty(cum) });
    cursor = nextDay(cursor);
    guard += 1;
  }
  return out;
}

export function fortnight(rows: ReportRow[], now: number): DayBar[] {
  const days: DayBar[] = [];
  for (let i = 13; i >= 0; i -= 1) {
    const key = dayKey(now - i * 24 * 60 * 60 * 1000);
    const group = rows.filter((row) => rowDay(row) === key);
    days.push({
      key,
      label: key.slice(8),
      taken: group.length,
      hit: group.filter((row) => row.mark === 'hit').length,
    });
  }
  return days;
}
